import * as noUiSlider from 'nouislider';
import 'nouislider/dist/nouislider.css';
import { pluralize, debounce, decodeEntities } from '../misc/utils';

export default class {
  constructor(sliderContainer) {
    this.onChangeCallback = null;
    this.onSlideCallback = null;
    this.sliderContainer = sliderContainer;
    this.slider = sliderContainer.querySelector('.js-range-slider__slider');
    this.showValueAsTooltip = this.slider.dataset.showValueAsTooltip;
    this.showDisabledTooltip = this.slider.dataset.showDisabledTooltip;
    this.showMaxValueTooltip = this.slider.dataset.showMaxValueTooltip;
    this.sliderMin = this.slider.dataset.min;
    this.sliderMax = this.slider.dataset.max;
    this.sliderStep = this.slider.dataset.step;
    this.sliderStart = this.slider.dataset.start || this.sliderMin;
    this.type = this.slider.dataset.type;
    this.unitSingular = this.slider.dataset.unitsingular;
    this.unitPlural234 = this.slider.dataset.unitplural234 || this.unitSingular;
    this.unitPlural056 = this.slider.dataset.unitplural056 || this.unitSingular;
    this.sliderFirstStep = parseInt(this.slider.dataset.firststep);
    this.sliderDisabled = this.slider.dataset.disabled;
    this.sliderMaxTooltip = this.slider.dataset.maxtooltip;
    this.sliderTooltipInstance = this.slider.parentNode._tippy;
    this.noOffsetSubtraction = this.slider.dataset.noOffsetSubtraction === 'true';
    // Discrete, irregularly spaced steps: the slider runs on indexes and the
    // label comes from this array instead of the numeric value plus unit.
    this.valueLabels = this.slider.dataset.valueLabels ? JSON.parse(this.slider.dataset.valueLabels) : null;

    let noUiSliderOptions = {
      start: this.sliderStart,
      connect: 'lower',
      tooltips: this.showValueAsTooltip === 'true',
      format: {
        from: Number,
        to: function (value) {
          return this.formatTooltipValue(value);
        }.bind(this),
      },
    };

    // Enable tooltip if slider is disabled and tooltip is set
    if (this.sliderDisabled === 'true' && this.showDisabledTooltip && this.sliderTooltipInstance) {
      this.sliderTooltipInstance.enable();
    }

    if (this.sliderMin === this.sliderMax) {
      noUiSliderOptions.start = parseInt(this.sliderMax);
      noUiSliderOptions.range = {
        min: [parseInt(this.sliderMin)],
        max: [parseInt(this.sliderMax)],
      };
    } else if (this.sliderFirstStep !== 0) {
      noUiSliderOptions.range = {
        min: [parseInt(this.sliderMin), parseInt(this.sliderFirstStep)],
        '1%': [parseInt(this.sliderFirstStep), parseInt(this.sliderStep)],
        max: [parseInt(this.sliderMax)],
      };
    } else {
      noUiSliderOptions.range = {
        min: [parseInt(this.sliderMin)],
        max: [parseInt(this.sliderMax)],
      };
      noUiSliderOptions.step = parseInt(this.sliderStep);
    }

    noUiSlider.create(this.slider, noUiSliderOptions);

    if (this.sliderDisabled === 'true') {
      this.slider.setAttribute('disabled', true);
    }

    this.handleChangeDebounced = debounce(this.handleChange.bind(this), 250);
    this.handleSlideDebounced = debounce(this.handleSlide.bind(this), 100);

    this.slider.noUiSlider.on('change', this.handleChangeDebounced.bind(this));
    this.slider.noUiSlider.on('slide', this.handleSlideDebounced.bind(this));
  }

  handleChange(values, handle, unencoded, tap, positions, noUiSlider) {
    if (this.showValueAsTooltip !== 'true') {
      this.updateUnitLabelText(unencoded[0]);
    }

    if (typeof this.onChangeCallback === 'function') {
      this.onChangeCallback(values, handle, unencoded, tap, positions, noUiSlider);
    }
  }

  handleSlide(values, handle, unencoded, tap, positions, noUiSlider) {
    if (this.showValueAsTooltip !== 'true') {
      this.updateUnitLabelText(unencoded[0]);
    }

    if (typeof this.onSlideCallback === 'function') {
      this.onSlideCallback(values, handle, unencoded, tap, positions, noUiSlider);
    }

    if (this.showMaxValueTooltip === 'true') {
      this.updateMaxValueTooltip(unencoded[0]);
    }
  }

  onChange(callback) {
    this.onChangeCallback = callback;
  }

  onSlide(callback) {
    this.onSlideCallback = callback;
  }

  getValue() {
    const parentEl = this.slider.querySelector('[role="slider"]');
    if (parentEl) {
      const value = parentEl.getAttribute('aria-valuenow');
      return parseFloat(value);
    }
    return 0;
  }

  setPrice(valueVat, value) {
    const priceEl = this.sliderContainer.querySelector('.js-range-slider__price');
    if (!priceEl) {
      return false;
    }

    priceEl.dispatchEvent(
      new CustomEvent('update-prices', {
        detail: {
          price: decodeEntities(value),
          priceVat: decodeEntities(valueVat),
        },
      }),
    );
  }

  updateMaxValueTooltip(currentValue) {
    if (currentValue == this.sliderMax) {
      this.sliderTooltipInstance.enable();
      this.sliderTooltipInstance.show();
    } else {
      this.sliderTooltipInstance.disable();
    }
  }

  updateUnitLabelText(value) {
    const tooltipEl = this.sliderContainer.querySelector('.js-range-slider__unit-label');
    if (tooltipEl) {
      tooltipEl.textContent = this.formatTooltipValue(value);
    }
  }

  formatTooltipValue(value) {
    if (this.valueLabels) {
      return this.valueLabels[Math.round(value)] ?? '';
    }

    return `${Math.round(value)} ${pluralize(value, this.unitSingular, this.unitPlural056, this.unitPlural234)}`;
  }

  setMinMax(min, max) {
    // Get current value before updating
    let currentValue = this.getValue();

    // Update internal min and max values - ensure they're numbers
    this.sliderMin = parseFloat(min) || 0;
    this.sliderMax = parseFloat(max) || 0;

    // Ensure current value is within new range, default to min if invalid
    if (isNaN(currentValue) || currentValue < this.sliderMin || currentValue > this.sliderMax) {
      currentValue = this.sliderMin;
    }

    let updateOptions = {
      start: currentValue, // Use current value instead of min
    };

    if (this.sliderMin === this.sliderMax) {
      updateOptions.start = parseInt(this.sliderMax);
      updateOptions.range = {
        min: [parseInt(this.sliderMin)],
        max: [parseInt(this.sliderMax)],
      };
    } else if (this.sliderFirstStep !== 0) {
      updateOptions.range = {
        min: [parseInt(this.sliderMin), parseInt(this.sliderFirstStep)],
        '1%': [parseInt(this.sliderFirstStep), parseInt(this.sliderStep)],
        max: [parseInt(this.sliderMax)],
      };
    } else {
      updateOptions.range = {
        min: [parseInt(this.sliderMin)],
        max: [parseInt(this.sliderMax)],
      };
      updateOptions.step = parseInt(this.sliderStep);
    }

    try {
      this.slider.noUiSlider.updateOptions(updateOptions);
    } catch (error) {
      console.error('Error updating slider options:', error);
    }
  }

  setStep(step) {
    try {
      // Get current value before updating
      let currentValue = this.getValue();

      // Update the internal step value - ensure it's a number
      this.sliderStep = parseFloat(step) || 1;

      // Get current min and max values
      const currentMin = this.sliderMin;
      const currentMax = this.sliderMax;

      // Ensure current value is valid, default to min if invalid
      if (isNaN(currentValue) || currentValue < currentMin || currentValue > currentMax) {
        currentValue = currentMin;
      } else {
        // Adjust current value to fit new step constraints
        const stepValue = parseFloat(this.sliderStep);
        const minValue = parseFloat(currentMin);
        const adjustedValue = Math.round((currentValue - minValue) / stepValue) * stepValue + minValue;

        if (adjustedValue >= currentMin && adjustedValue <= currentMax) {
          currentValue = adjustedValue;
        } else {
          currentValue = currentMin;
        }
      }

      // Update slider options with new step
      if (this.sliderFirstStep !== 0 && this.sliderFirstStep !== undefined) {
        // Use range configuration with first step
        const updateOptions = {
          start: currentValue,
          range: {
            min: [parseFloat(currentMin), parseFloat(this.sliderFirstStep)],
            '1%': [parseFloat(this.sliderFirstStep), parseFloat(this.sliderStep)],
            max: [parseFloat(currentMax)],
          },
        };
        this.slider.noUiSlider.updateOptions(updateOptions);
      } else {
        // Use simple step configuration
        const updateOptions = {
          start: currentValue,
          range: {
            min: [parseFloat(currentMin)],
            max: [parseFloat(currentMax)],
          },
          step: parseFloat(this.sliderStep),
        };
        this.slider.noUiSlider.updateOptions(updateOptions);
      }
    } catch (error) {
      console.error('Error setting slider step:', error, 'Step:', step, 'Slider:', this);
    }
  }

  setDefaultDisplay(defaultValue) {
    try {
      // Update the unit label to show the default value
      const tooltipEl = this.sliderContainer.querySelector('.js-range-slider__unit-label');
      if (tooltipEl) {
        tooltipEl.textContent = this.formatTooltipValue(defaultValue);
      }

      // If tooltip is enabled, update it as well
      if (this.showValueAsTooltip === 'true' && this.slider.noUiSlider) {
        // Get the tooltip element and update its content
        const tooltipElement = this.slider.querySelector('.noUi-tooltip');
        if (tooltipElement) {
          const formattedValue = this.formatTooltipValue(defaultValue);
          tooltipElement.textContent = formattedValue;
        }
      }
    } catch (error) {
      console.error('Error setting default display:', error, 'Default value:', defaultValue, 'Slider:', this);
    }
  }
}
